import { useForm } from "@tanstack/react-form";
import * as v from "valibot";
import { initialConfig } from "../initialConfig.ts";

const NumberInput = v.union([v.number(), v.string()]);

const invalidNumberMessage =
  "pole musí být validní celé nebo desetinné číslo nebo prázdné pole";

export const resultsSchema = v.object({
  AssetsCategoryId: v.undefinedable(v.number()),
  RegionId: v.undefinedable(v.number()),
  Id: v.undefinedable(v.number()),
  Price: v.undefinedable(
    v.undefinedable(
      v.pipe(
        NumberInput,
        v.transform((input) =>
          input.toString().replace(/,/g, ".").replace(/\s+/g, ""),
        ),
        v.decimal(invalidNumberMessage),
        v.transform((input) => {
          // First, replace commas with periods and all whitespace with nothing
          let cleanedInput = input
            .toString()
            .replace(/,/g, ".")
            .replace(/\s+/g, "");

          // Optional: Check for edge cases like non-breaking spaces
          cleanedInput = cleanedInput.replace(/\u00A0/g, ""); // Non-breaking space (Unicode 0xA0)

          // Check if the cleaned input is a valid number
          const parsedNumber = Number(cleanedInput);

          // If the result is NaN, return 0
          if (Number.isNaN(parsedNumber)) {
            return 0;
          }

          return parsedNumber;
        }),
      ),
    ),
  ),
  DetailCategoryFilter: v.undefinedable(
    v.object({
      Znacka: v.undefinedable(v.string()),
      RokVyrobyOd: v.undefinedable(
        v.pipe(
          NumberInput,
          v.transform((input) =>
            input.toString().replace(/,/g, ".").replace(/\s+/g, ""),
          ),
          v.decimal(invalidNumberMessage),
          v.transform((input) => {
            const numberInput = input
              .toString()
              .replace(/,/g, ".")
              .replace(/\s+/g, "");

            return Number(numberInput);
          }),
        ),
      ),
      RokVyrobyDo: v.undefinedable(
        v.pipe(
          NumberInput,
          v.transform((input) =>
            input.toString().replace(/,/g, ".").replace(/\s+/g, ""),
          ),
          v.decimal(invalidNumberMessage),
          v.transform((input) => {
            const numberInput = input
              .toString()
              .replace(/,/g, ".")
              .replace(/\s+/g, "");

            return Number(numberInput);
          }),
        ),
      ),
      Prevodovka: v.undefinedable(v.string()),
      NajetoDo: v.undefinedable(
        v.pipe(
          NumberInput,
          v.transform((input) =>
            input.toString().replace(/,/g, ".").replace(/\s+/g, ""),
          ),
          v.decimal(invalidNumberMessage),
          v.transform((input) => {
            const numberInput = input
              .toString()
              .replace(/,/g, ".")
              .replace(/\s+/g, "");

            return Number(numberInput);
          }),
        ),
      ),
      Palivo: v.undefinedable(v.string()),
    }),
  ),
  sort: v.undefinedable(
    v.picklist([
      "ExpirationAsc",
      "ExpirationDesc",
      "Newest",
      "Oldest",
      "AlphabeticalAsc",
      "AlphabeticalDesc",
      "Cheapest",
      "MostExpensive",
    ]),
  ),
  page: v.undefinedable(v.number()),
  pageSize: v.undefinedable(v.number()),
});

function parseFilter(uri: string): ({
  Id?: number,
  AssetsCategoryId?: number,
  RegionId?: number,
  Price?: number,
  DetailCategoryFilter?: {
    NajetoDo?: number,
    Palivo?: string,
    Prevodovka?: string,
    // RokVyrobyOd?: string,
    // RokVyrobyDo?: string,
    RokUvedeniDoProvozuDo?: number,
    RokUvedeniDoProvozuOd?: number,
    Znacka?: string,
  },
  sort?: string,
  pageSize?: number,
  page?: number,
}) {
  const decoded = decodeURIComponent(uri);
  const withoutHash = decoded.substring(1);
  try {
    const json = JSON.parse(withoutHash);
    return json;
  } catch (e) {
    return {};
  }
};

export const useOverview = () => {

  // initial data from page load
  const initialDataHash = initialConfig.parentHashFromHandshake ?? "";
  const initialData = parseFilter(initialDataHash);

  return useForm({
    // Needs to be set to 0 for TS Server to register their existence. These 0 and "" are later removed when sending the query to
    defaultValues: {
      Id: 0,
      AssetsCategoryId: initialData.AssetsCategoryId ?? 0,
      RegionId: initialData.RegionId ?? 0,
      Price: initialData.Price ?? 0,
      DetailCategoryFilter: {
        NajetoDo: initialData?.DetailCategoryFilter?.NajetoDo ?? "",
        Palivo: initialData?.DetailCategoryFilter?.Palivo ?? "",
        Prevodovka: initialData?.DetailCategoryFilter?.Prevodovka ?? "",
        RokUvedeniDoProvozuDo: initialData?.DetailCategoryFilter?.RokUvedeniDoProvozuDo ?? "",
        RokUvedeniDoProvozuOd: initialData?.DetailCategoryFilter?.RokUvedeniDoProvozuOd ?? "",
        Znacka: initialData?.DetailCategoryFilter?.Znacka ?? "",
        // NajetoDo: "",
        // Palivo: "",
        // Prevodovka: "",
        // RokVyrobyOd: "",
        // Znacka: "",
      },
      sort: initialData?.sort ?? "ExpirationAsc",
      pageSize: initialData?.pageSize ?? 25,
      page: initialData?.page ?? 0,
      // sort: "ExpirationAsc",
      // pageSize: 25,
      // page: 0,
    },
    validators: {
      onChange: (values) => {
        try {
          v.parse(resultsSchema, values);
          return { status: "success" };
        } catch (error) {
          return {
            status: "error",
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },
      onSubmit: (values) => {
        try {
          v.parse(resultsSchema, values);
          return { status: "success" };
        } catch (error) {
          return {
            status: "error",
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },
    },
  });
};
export type OverviewForm = ReturnType<typeof useOverview>;
